import { message, Modal } from 'antd'
import { MessageInstance } from 'antd/es/message/interface'
import CantileverButton from 'components/CantileverButton'
import {
  createEditedObject,
  createUniqueFormFields,
} from 'components/form/formHelpers'
import {
  FormFieldsByUniqueID,
  SimpleFormField,
} from 'components/form/formTypes'
import { GroupingInfoJSONFromFormFields } from 'features/groupings/groupingInfoFormFields'
import React, { RefObject } from 'react'
import { SubmitHandler } from 'react-hook-form'
import styled from 'styled-components'
import { GeckoSite, RolodexEntryPatchJson, RolodexEntryType } from 'types'
import { v4 as uuid } from 'uuid'

import { GroupingInfoForm } from './GroupingInfoForm'

const FormContainer = styled.div`
  flex-direction: column;
  padding-top: 1rem;
  gap: 0;
`

export interface GroupingInfoModalProps {
  site: GeckoSite | undefined
  title: string
  groupingInfoFormFields: FormFieldsByUniqueID[]
  groupingID: string | undefined
  isGroupingInfoModalOpen: boolean
  groupingFormRef: RefObject<HTMLFormElement>
  handleCloseGroupingInfoModal: () => void
  handleSaveGroupingInfo: () => void
  handlePatchToRolodex: (
    entry: RolodexEntryPatchJson<
      Partial<GeckoSite['data']>,
      Partial<GeckoSite['tags']>
    >,
    messageApi?: MessageInstance
  ) => void
  handleCancelGroupingInfo: () => void
}

export const GroupingInfoModal: React.FC<GroupingInfoModalProps> = ({
  site,
  title,
  groupingInfoFormFields,
  groupingID,
  isGroupingInfoModalOpen,
  groupingFormRef,
  handleCloseGroupingInfoModal,
  handleSaveGroupingInfo,
  handlePatchToRolodex,
  handleCancelGroupingInfo,
}) => {
  const [messageApi, contextHolder] = message.useMessage()

  const onSubmit: SubmitHandler<SimpleFormField> = (data) => {
    if (!site) {
      messageApi?.error('Missing site information')
      return
    }
    const convertedFormFields = createEditedObject(
      data,
      createUniqueFormFields(groupingInfoFormFields)
    )
    const groupingId = groupingID || uuid().slice(0, 6)
    const groupingInfoJSON = GroupingInfoJSONFromFormFields(
      convertedFormFields[0].formFields
    )
    const updatedGroupings = {
      ...site.data.groupings,
      [groupingId]: {
        ...groupingInfoJSON,
        id: groupingId,
      },
    }
    const entry = {
      id: site.id,
      type: RolodexEntryType.SITE,
      data: {
        groupings: updatedGroupings,
      },
    }

    handlePatchToRolodex(entry, messageApi)
    handleCloseGroupingInfoModal()
  }

  return (
    <>
      {contextHolder}
      <Modal
        title={title}
        open={isGroupingInfoModalOpen}
        width='80%'
        onCancel={handleCancelGroupingInfo}
        footer={
          <CantileverButton primary onClick={handleSaveGroupingInfo}>
            Save Grouping
          </CantileverButton>
        }>
        <FormContainer>
          <GroupingInfoForm
            formFields={groupingInfoFormFields}
            inEditMode={true}
            ref={groupingFormRef}
            onSubmit={onSubmit}
          />
        </FormContainer>
      </Modal>
    </>
  )
}
